/**
 * The agent execution gate — a **blunt raw-token scan**.
 *
 * When an AI CLI agent asks the app to run SQL, this decides whether the app
 * may execute it silently or must show a confirmation. Under an active
 * "Always for this session" grant there is no toast, so `isAgentDestructiveQuery`
 * is the ONLY consent surface between an agent and a DELETE.
 *
 * **One pass over the raw query text. No lexer, no stripping, no splitting.**
 * That is not a simplification, it is the security property:
 *
 *   The previous design scanned SQL *after* removing comments and string
 *   literals, so `SELECT 'DROP TABLE x'` would not raise a false alarm.
 *   Removal requires knowing where comments and literals begin and end, and
 *   that differs per engine (`#` on MySQL only; backslash escapes on MySQL
 *   only; nested block comments on PG and MSSQL; version-gated executable
 *   comments on MySQL; dollar quoting; Oracle alternative quoting). **Every
 *   disagreement between the remover's opinion and the engine's opinion is a
 *   bypass** — the remover "sees" a comment the engine does not, the
 *   destructive statement vanishes from the scan, and it runs. Five fix rounds
 *   produced five Criticals, all that same shape, four of them introduced by
 *   the previous round's fix.
 *
 * Because nothing is removed here, there is no removal for a construct to hide
 * behind. **What that buys, exactly:** a statement that *spells* a destructive
 * verb carries that verb in the raw text no matter how it is spelled, so it
 * cannot be hidden by a quoting or comment construct this app models
 * differently from the engine. Measured against the 37-payload corpus in
 * `.superpowers/sdd/AiSmoothnessWave-Plan/bypass-corpus.md`: 37/37, against
 * 10/37 for the lexer-based build this replaced.
 *
 * **What it does not buy, and an earlier version of this comment wrongly
 * claimed it did:** a statement that *invokes* a routine carries the routine's
 * name, not the verb inside it. Executed live against the five containers, each
 * against a scratch table, rows observed before and after — every one took the
 * table from 3 rows to 0 with this gate returning `execute`:
 *
 * | engine | statement | why no token is present |
 * |---|---|---|
 * | pgsql | `SELECT gate_review_cleanup()` | the verb is in the function body |
 * | mssql | `dbo.gate_review_prune` | T-SQL runs a proc with no `EXEC` when it is first in the batch |
 * | oracle | `BEGIN gate_review_prune; END;` | anonymous block, no `CALL`/`EXEC` |
 * | mysql | `SELECT gate_review_fn()` | as pgsql |
 * | sqlite | `INSERT INTO q (id) VALUES (1)` | an `AFTER INSERT` trigger does the deleting |
 *
 * This is a **pre-existing residual, not a regression** — the lexical build was
 * silent on all five too, and closing it needs a parser plus a catalogue of
 * side-effecting routines, not another table entry. It is recorded as the
 * design's known limit in the plan (§10, D-10) and routed to CP-C. Do not widen
 * this table to chase it, and do not re-add the sentence that said it could not
 * happen.
 *
 * The one table this scans with is `agentTriggers.ts` — one array, and the
 * matcher is generated from it, so there is no second list to drift from.
 *
 * **Do not add normalisation.** Not comment stripping, not literal blanking,
 * not statement splitting on `;` or `GO`, not a per-engine table. Each one
 * re-opens the whole bug class. The excerpt shown in the dialog is computed
 * *after* the decision, from the match offset, so it changes no verdict.
 *
 * The cost is paid on SQL-as-data: a query whose *text* names a verb confirms
 * — `WHERE subject = 'Cannot update my billing address'` raises a dialog. That
 * cost was already being paid by the build this replaces (95 % of a narration
 * corpus), which is the argument for dropping the machinery that was supposed
 * to prevent it.
 */

import type {
  DatabaseEngineId,
  DangerousQueryResult,
  DangerousQueryKind,
} from './engineTypes';
import {
  AGENT_TRIGGERS,
  buildAgentTriggerMatcher,
  type AgentTrigger,
  type AgentTriggerKind,
} from './agentTriggers';

// Re-exported so callers and tests keep naming one module for the gate.
export { AGENT_TRIGGERS, buildAgentTriggerMatcher };
export type { AgentTrigger, AgentTriggerKind };

const AGENT_TRIGGER_MATCHER = buildAgentTriggerMatcher(AGENT_TRIGGERS);

const REASONS: Record<AgentTriggerKind, string> = {
  destructive: 'This statement was written by an AI agent and it modifies or drops data',
  'opaque-execution':
    'This statement was written by an AI agent and it runs SQL this app cannot inspect',
};

/** The analytics-safe half of the same verdict — see `DangerousQueryKind`. */
const KINDS: Record<AgentTriggerKind, DangerousQueryKind> = {
  destructive: 'agent-destructive',
  'opaque-execution': 'agent-opaque',
};

/**
 * Which entry produced a given match. Exact, not a second matcher: a
 * boundary-anchored alternative can only match its own token, and the one bare
 * token (`EXEC`) is not a substring of any other entry.
 */
const triggerFor = (matched: string): AgentTrigger | undefined =>
  AGENT_TRIGGERS.find((trigger) => trigger.token.toLowerCase() === matched.toLowerCase());

/**
 * What the dialog shows. Display only, computed from the match offset **after**
 * the verdict — so it starts at the offending token rather than at the harmless
 * statement in front of it, without the gate ever splitting the input.
 */
const excerptFrom = (query: string, at: number): string =>
  query.slice(at).trim().replace(/\s+/g, ' ').substring(0, 100);

/**
 * Is this query destructive **by the stricter standard applied to SQL an
 * autonomous agent wrote**?
 *
 * The human risk gate (`checkRisk`) treats `DELETE`/`UPDATE` as dangerous only when the WHERE
 * clause is missing. That heuristic is calibrated for a human typing their own
 * SQL, and it is far too weak when the author is an agent: `DELETE FROM users
 * WHERE 1=1` sails through it, and that is the exact payload the
 * execute-with-confirm consent bypass used.
 *
 * **This function deliberately over-confirms.** On the granted path it is the
 * only consent surface there is, and the two outcomes are not symmetric: a
 * false positive costs one extra dialog on a path the user already knows is
 * agent-driven, while a false negative is unreviewed destruction.
 *
 * It is **additive**: it never relaxes anything, the human gate is
 * untouched, and the human execution path never calls it.
 *
 * The engine id is accepted and **deliberately unused**. Per-engine branching
 * is what the removed lexer did, and every per-engine row that disagreed with
 * its engine was a bypass. The same scan runs on all six ids.
 */
export const isAgentDestructiveQuery = (
  _id: DatabaseEngineId,
  query: string
): DangerousQueryResult => {
  const match = AGENT_TRIGGER_MATCHER.exec(query);
  if (!match) return { dangerous: false };

  const trigger = triggerFor(match[0]);

  return {
    dangerous: true,
    severity: 'high',
    // `triggerFor` cannot miss for a match this matcher produced; the fallback
    // is the safe direction anyway.
    reason: REASONS[trigger?.kind ?? 'destructive'],
    kind: KINDS[trigger?.kind ?? 'destructive'],
    statement: excerptFrom(query, match.index),
  };
};
